import { captureException } from '@sentry/sveltekit';

/**
 * Patch the course user data
 * @param {Course} course - The course object
 * @param {import("@supabase/supabase-js").SupabaseClient} supabase - The supabase client
 * @returns {Promise<Course>} - The course object
 */
export async function patch_course_user_data(course, supabase) {
    let res = [];

    try {
        const { data, error } = await supabase
            .from('Courses')
            .select(
                `
                id,
                User_Video_Progress(video_id, progress, complete),
                User_Answers(lesson_id, question_text, question_index, answer_text, is_correct),
                User_Courses_Reviews(review_text, review_rating, username),
                User_Purchases(status),
                User_Lesson_Progress(lesson_id)
            `
            )
            .eq('id', course.id);

        if (error) {
            console.error(error);
            captureException(error, {
                extra: { message: 'Supabase error patching course user data' }
            });
            return course;
        }

        res = data;
    } catch (error) {
        console.error(error);
        captureException(error, {
            extra: { message: 'Error patching course user data' }
        });
        return course;
    }

    course.purchased = res[0].User_Purchases.some(
        (p) => p.status === 'purchased'
    );

    if (!course.purchased) {
        return course;
    }

    course.lessons = course.lessons.map((lesson) => {
        lesson.locked = false;

        if (lesson.type === 'video' && lesson.videos.length) {
            lesson.complete = true;
            lesson.videos = lesson.videos.map((v) => {
                const video =
                    res[0].User_Video_Progress.find(
                        (prog) => prog.video_id === v.video_id
                    ) || {};

                const new_v = { ...v, ...video };

                if (!new_v.complete) {
                    lesson.complete = false;
                    course.complete = false;
                }

                return new_v;
            });

            course.videos.push(...lesson.videos);
            course.complete = course.videos.every((v) => v.complete);
        }

        if (lesson.type === 'quiz' || lesson.type === 'video') {
            lesson.questions = lesson.questions.map((q) => {
                const question =
                    res[0].User_Answers.find(
                        (a) =>
                            a.lesson_id === lesson.id &&
                            a.question_index === q.question_index
                    ) || {};

                return { ...q, ...question };
            });
        }

        if (lesson.type === 'quiz') {
            lesson.complete = lesson.questions.every(
                (q) => q.answer_text !== null || !q.question_type
            );
        }

        if (lesson.type === 'social_media_simulator') {
            lesson.complete = res[0].User_Lesson_Progress.some(
                (p) => p.lesson_id === lesson.id
            );
        }

        return lesson;
    });

    course.complete = course.lessons.every((l) => l.complete);

    course.review = res[0].User_Courses_Reviews[0] || null;

    return course;
}

/**
 * Patch the worksheet user data
 * @param {Worksheet} worksheet - The worksheet object
 * @param {import("@supabase/supabase-js").SupabaseClient} supabase - The supabase client
 * @returns {Promise<Worksheet>} - The worksheet object
 */
export async function patch_worksheet_user_data(worksheet, supabase) {
    try {
        const { data, error } = await supabase
            .from('Worksheets')
            .select(
                `
                id,
                User_Worksheets_Reviews(review_text, review_rating, username),
                User_Worksheet_Purchases(status)
            `
            )
            .eq('id', worksheet.id);

        if (error) {
            console.error(error);
            captureException(error, {
                extra: {
                    message: 'Supabase error patching worksheet user data'
                }
            });
            return worksheet;
        }

        worksheet.purchased =
            data?.[0]?.User_Worksheet_Purchases?.some(
                (p) => p.status === 'purchased'
            ) || false;

        if (data[0].User_Worksheets_Reviews.length) {
            worksheet.review = {
                review_text: data[0].User_Worksheets_Reviews[0].review_text,
                review_rating: data[0].User_Worksheets_Reviews[0].review_rating,
                username: data[0].User_Worksheets_Reviews[0].username
            };
        }
    } catch (error) {
        console.error(error);
        captureException(error, {
            extra: { message: 'Error patching worksheet user data' }
        });
        return worksheet;
    }

    if (worksheet.purchased) {
        worksheet.files = await get_worksheet_files(
            worksheet.id,
            worksheet.filenames,
            supabase
        );
    }

    return worksheet;
}

/**
 * Get the worksheet files
 * @param {string} worksheet_id - The worksheet id
 * @param {string[]} files - The files
 * @param {import("@supabase/supabase-js").SupabaseClient} supabase - The supabase client
 * @returns {Promise<string[]>} - The signed urls
 */
export async function get_worksheet_files(worksheet_id, files = [], supabase) {
    if (!worksheet_id) return [];

    let supabaseData;

    let fpaths = files.map((f) => `${worksheet_id}/${f}`);

    try {
        let { data, error } = await supabase.storage
            .from('sl-worksheets')
            .createSignedUrls(fpaths, 60 * 60 * 24 * 7);

        if (error) {
            console.error(error);
            captureException(error, {
                extra: { message: 'Supabase error getting worksheet files' }
            });
            return [];
        }
        supabaseData = data;
    } catch (error) {
        console.error(error);
        captureException(error, {
            extra: { message: 'Error getting worksheet files' }
        });
        return [];
    }

    return supabaseData.map((record) => record.signedUrl);
}
